import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { DnaBackground } from "@/components/DnaBackground";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";
import { useTextDirection } from "@/lib/direction";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const { token: pathToken } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const direction = useTextDirection();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('');
  const [userRole, setUserRole] = useState<'user' | 'admin'>('user');
  
  // Support both path parameter and query parameter for backwards compatibility
  const token = pathToken || searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyEmail(token);
    } else {
      setStatus('error');
      setMessage(t("errors.invalidVerificationLink"));
    }
  }, [token]);

  const verifyEmail = async (verificationToken: string) => {
    try {
      const response = await fetch(`${API_URL}/auth/verify-email/${verificationToken}`, {
        method: 'GET',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus('success');
        setMessage(t("emailVerification.verified"));
        setUserRole(data.data?.user?.role || 'user');
        
        // Store user data and token
        if (data.data?.token) {
          localStorage.setItem('authToken', data.data.token);
        }
        if (data.data?.user) {
          localStorage.setItem('userData', JSON.stringify(data.data.user));
        }

        toast.success(t("emailVerification.verified"));

        // Redirect after 3 seconds
        setTimeout(() => {
          const inviteToken = localStorage.getItem("invite_token");

          if (inviteToken) {
            navigate(`/invite/${inviteToken}`);
            return;
          }

          if (data.data?.user?.role === 'admin') {
            navigate('/admin');
          } else {
            navigate('/dashboard');
          }
        }, 3000);
      } else {
        setStatus('error');
        const errorMessage = getLocalizedErrorMessage(data.message, "errors.verificationFailed");
        setMessage(errorMessage);
        toast.error(errorMessage);
      }
    } catch (error: any) {
      setStatus('error');
      const errorMessage = getLocalizedErrorMessage(error, "errors.verificationError");
      setMessage(errorMessage);
      toast.error(errorMessage);
      console.error('Verification error:', error);
    }
  };

  const handleResendVerification = () => {
    navigate('/auth');
    toast.info(t("emailVerification.signUpAgain"));
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative" dir={direction}>
      <DnaBackground />
      
      <Card className="w-full max-w-md p-8 bg-card border-border shadow-2xl animate-fade-in">
        <div className="flex flex-col items-center text-center space-y-6">
          {status === 'verifying' && (
            <>
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
              </div>
              <div>
                <h1 className="text-2xl font-bold mb-2">Verifying Your Email</h1>
                <p className="text-muted-foreground">
                  Please wait while we verify your email address...
                </p>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center animate-scale-in">
                <CheckCircle2 className="w-10 h-10 text-green-500" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-green-500">Email Verified!</h1>
                <p className="text-muted-foreground">{message}</p>
                <div className="pt-4">
                  <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Redirecting you to {userRole === 'admin' ? 'admin panel' : 'dashboard'}...
                  </div>
                </div>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center animate-scale-in">
                <XCircle className="w-10 h-10 text-destructive" />
              </div>
              <div className="space-y-4">
                <div>
                  <h1 className="text-2xl font-bold text-destructive mb-2">
                    {t("errors.verificationFailed")}
                  </h1>
                  <p className="text-muted-foreground">{message}</p>
                </div>
                <div className="flex flex-col gap-3 pt-2">
                  <Button 
                    onClick={handleResendVerification}
                    className="w-full bg-primary hover:bg-primary/90"
                  >
                    Back to Sign Up
                  </Button>
                  <Button 
                    onClick={() => navigate('/')}
                    variant="outline"
                    className="w-full"
                  >
                    Go to Home
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
